import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { CardColor } from '../types';

interface TutorialGhostArrowProps {
  start: { x: number; y: number };
  end: { x: number; y: number };
  color: CardColor;
}

const TutorialGhostArrow: React.FC<TutorialGhostArrowProps> = ({ start, end, color }) => {
  const [progress, setProgress] = useState(0);
  const portalRoot = document.getElementById('portal-root');

  useEffect(() => {
    let startTime = Date.now();
    const duration = 2500; // Total cycle duration
    
    const animate = () => {
      const now = Date.now();
      const elapsed = (now - startTime) % duration;
      const p = elapsed / duration;
      
      // Animation logic:
      // 0.0 - 0.7: Move from start to end
      // 0.7 - 0.9: Hold at end
      // 0.9 - 1.0: Vanish (progress > 1)
      
      if (p < 0.7) {
        setProgress(p / 0.7);
      } else if (p < 0.9) {
        setProgress(1);
      } else {
        setProgress(1.1); // vanish
      }
      
      requestAnimationFrame(animate);
    };
    
    const frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [start.x, start.y, end.x, end.y]);

  if (!portalRoot || progress > 1) return null;

  const colorMap: Record<CardColor, string> = {
    [CardColor.Red]: '#ef4444',
    [CardColor.Pink]: '#ec4899',
    [CardColor.Green]: '#22c55e',
    [CardColor.Black]: '#94a3b8',
    [CardColor.Purple]: '#a855f7',
    [CardColor.Yellow]: '#eab308',
  };
  const strokeColor = colorMap[color] || '#ffffff';

  const curEnd = {
    x: start.x + (end.x - start.x) * progress,
    y: start.y + (end.y - start.y) * progress
  };

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy) || 1;
  const curveFactor = Math.min(80, (distance / 5)) * progress;
  
  const midX = (start.x + curEnd.x) / 2;
  const midY = (start.y + curEnd.y) / 2;
  const perpX = -dy / distance;
  const perpY = dx / distance;
  const controlX = midX + perpX * curveFactor;
  const controlY = midY + perpY * curveFactor;

  const pathData = `M ${start.x} ${start.y} Q ${controlX} ${controlY} ${curEnd.x} ${curEnd.y}`;

  return ReactDOM.createPortal(
    <svg className="fixed inset-0 pointer-events-none w-full h-full" style={{ zIndex: 9998, opacity: 1 }}>
      <defs>
        <filter id="ghost-glow" x="-50%" y="-50%" width="200%" height="200%">
          {/* Inner solid outline */}
          <feDropShadow dx="0" dy="0" stdDeviation="0.8" flood-color="black" flood-opacity="1"/>
          {/* Mid-transparency halo */}
          <feDropShadow dx="0" dy="0" stdDeviation="1.5" flood-color="black" flood-opacity="0.75"/>
          {/* Outer soft shadow */}
          <feDropShadow dx="0" dy="0" stdDeviation="2.5" flood-color="black" flood-opacity="0.5"/>
        </filter>
        <marker id="ghost-arrowhead" viewBox="-2 -5 10 10" refX="8" refY="0" markerWidth="6" markerHeight="6" orient="auto">
          <path d="M 0,-4 L 8,0 L 0,4" fill={strokeColor} />
        </marker>
      </defs>
      <path
        d={pathData}
        stroke={strokeColor}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeDasharray="8 8"
        markerEnd="url(#ghost-arrowhead)"
        style={{ 
            animation: 'marching-ants 0.5s linear infinite',
            filter: 'url(#ghost-glow)'
        }}
      />
      <g transform={`translate(${curEnd.x}, ${curEnd.y})`} style={{ filter: 'url(#ghost-glow)' }}>
        <circle r="12" stroke={strokeColor} strokeWidth="2" fill="none">
            <animate attributeName="r" values="10;16;10" dur="1.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.4;1" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <path d="M-8 0 L8 0 M0 -8 L0 8" stroke={strokeColor} strokeWidth="2.5" />
      </g>
    </svg>,
    portalRoot
  );
};

export default TutorialGhostArrow;
